import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import { DefaultContext } from '../../../../contexts/default';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import { demoPages } from '../../../../menu';
import { ACCESS_LEVEL } from '../../../../types/roles';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import SelectStore from '../../../../components/MyCustom/SelectStore';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import showNotification from '../../../../components/extras/showNotification';
import ModalRegisterTerminal from '../../../../components/modals/Terminal/ModalRegister';
import ModalDeleteTerminal from '../../../../components/modals/Terminal/ModalDelete';
import notificationBody from '../../../../helpers/utils/functions/notificationBody';
import useDarkMode from '../../../../hooks/useDarkMode';
import TerminaisDB from '../../../../database/wrappers/terminais';
import EstablishmentDB from '../../../../database/wrappers/establishment';
import Popovers from '../../../../components/bootstrap/Popovers';
import { where } from 'firebase/firestore';


const Terminal = () => {
  const { accessLevel, estabSelected, storeSelected, stores } = useContext(DefaultContext);
  const [openModalRegister, setOpenModalRegister] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [terminais, setTerminais] = useState([])
  const [totenSelected, setTotenSelected] = useState({})
  const [terminalSelectedDelete, setTerminalSelectedDelete] = useState(null)
  const [estabNames, setEstabNames] = useState({})
  const [storeNames, setStoreNames] = useState({})
  const [loading, setLoading] = useState(false)

  const { darkModeStatus } = useDarkMode()

  useEffect(() => {
    if (!estabSelected) {
      setTerminais([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const terminaisDB = new TerminaisDB(estabSelected);

    const constraints = [];
    if (storeSelected) {
      constraints.push(where('storeId', '==', storeSelected));
    }

    const unsubscribe = terminaisDB.onWithError((data) => {
      setTerminais(data || []);
      setLoading(false);
    }, (error) => {
      showNotification('', notificationBody(`Falha ao consultar terminais: ${error?.code || error?.message || ''}`), 'danger');
      setLoading(false);
    }, ...constraints);

    return () => {
      unsubscribe();
      setLoading(false);
    };
  }, [estabSelected, storeSelected]);

  useEffect(() => {
    const unsub = new EstablishmentDB().on((estabs) => {
      const map = {};
      estabs.forEach((e) => {
        map[e.id] = e.name;
      });
      setEstabNames(map);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const map = {};
    if (stores) {
      stores.forEach((s) => {
        map[s.id] = s.name;
      });
    }
    setStoreNames(map);
  }, [stores]);


  const handleOpenModalRegister = useCallback(() => {
    setTotenSelected(null)
    setOpenModalRegister(true);
  }, [])

  const handleCloseModalRegister = useCallback(() => {
    setOpenModalRegister(false);
  }, [])

  const handleOpenModalEdit = useCallback((item) => {
    setTotenSelected(item);
    setOpenModalRegister(true)
  }, [])

  const handleOpenModalDelete = useCallback((item) => {
    setTerminalSelectedDelete(item);
    setOpenModalDelete(true);
  }, [])

  const handleCloseModalDelete = useCallback(() => {
    setOpenModalDelete(false);
    setTerminalSelectedDelete(null);
  }, [])

  const columns = useMemo(
    () => [
      { label: 'Localização', field: 'location' },
      {
        label: 'Estabelecimento',
        field: 'estabId',
        format: (row) => estabNames[row.estabId] || row.estabId || '-',
      },
      {
        label: 'Loja',
        field: 'storeId',
        format: (row) => storeNames[row.storeId] || row.storeId || '-',
      },
      { label: 'Serial', field: 'serial' },
      {
        label: 'Status',
        field: 'active',
        format: (row) => (
          <>
            <Button
              isLink
              color={row.active ? 'success' : 'danger'}
              icon='Circle'
              className='text-nowrap'
            >
              {row.active ? 'Ativo' : 'Inativo'}
            </Button>
          </>
        ),
      },
      {
        label: 'Ações',
        field: 'actions',
        format: (row) => (
          <div className="d-flex gap-2">
            <Popovers trigger="hover"
              title="Editar">
              <Button color="dark" icon="Edit" size="sm" onClick={() => handleOpenModalEdit(row)} />
            </Popovers>
            {accessLevel === ACCESS_LEVEL.ADMIN && (
              <Popovers trigger="hover" title="Excluir">
                <Button color="danger" icon="Trash" size="sm" onClick={() => handleOpenModalDelete(row)} />
              </Popovers>
            )}
          </div>
        ),
        noExport: true,
      },
    ],
    [accessLevel, estabNames, storeNames, handleOpenModalEdit, handleOpenModalDelete]
  );

  return (
    <>
      <PageWrapper title={demoPages.pageLayout.subMenu.terminal.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb
              list={[
                { title: 'Cadastros', to: '/registers/terminal' },
                {
                  title: 'Terminais',
                  to: '/registers/terminal',
                },
              ]}
            />
          </SubHeaderLeft>
          <SubHeaderRight>
            <Button
              style={{ color: 'black' }}
              color='warning'
              icon='plus'
              shadow="sm"
              hoverShadow="sm"
              onClick={handleOpenModalRegister}>
              Cadastrar
            </Button>
          </SubHeaderRight>
        </SubHeader>
        <Page container='fluid'>
          <Card>
            <CardHeader>
              <CardLabel icon='Devices' iconColor={darkModeStatus ? 'light' : 'dark'}>
                <CardTitle>Terminais</CardTitle>
              </CardLabel>
            </CardHeader>
            <CardBody className='table-responsive p-5'>
              <div className='row'>
                <SelectEstablishment />
                <SelectStore />
              </div>
              {loading ? (
                <div className="text-center py-5">Carregando terminais...</div>
              ) : !estabSelected ? (
                <div className="text-center py-5 text-muted">
                  Selecione um estabelecimento para visualizar os terminais
                </div>
              ) : terminais.length === 0 ? (
                <div className="text-center py-5 text-muted">Nenhum terminal encontrado</div>
              ) : (
                <TableCustom
                  titlePdf="Terminais"
                  icon="Devices"
                  fontSize={8}
                  columns={columns}
                  rows={terminais}
                />
              )}
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalRegisterTerminal
        open={openModalRegister}
        setIsOpen={setOpenModalRegister}
        setIsClose={handleCloseModalRegister}
        terminalSelected={totenSelected}
      />

      {accessLevel === ACCESS_LEVEL.ADMIN && (
        <ModalDeleteTerminal
          open={openModalDelete}
          setIsOpen={setOpenModalDelete}
          setIsClose={handleCloseModalDelete}
          terminalSerial={terminalSelectedDelete?.serial}
        />
      )}
    </>
  );
};

export default Terminal;
